import { ButtonHTMLAttributes, useEffect, useRef, useState } from 'react'
import Flip from 'gsap/dist/Flip'
import Link from 'next/link'

import Button from '@/components/base/button'
import ButtonMask from '@/components/base/button-mask'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'

export type ButtonMoreProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  href: string
  target?: string
}

const ButtonMore = ({ href, target, children, className, ...props }: ButtonMoreProps) => {
  const [active, setActive] = useState(false)
  const rect = useRef<HTMLSpanElement>(null!)
  const wrapper = useRef<HTMLSpanElement>(null!)

  useEffect(() => {
    const state = Flip.getState(rect.current)
    wrapper.current.classList[active ? 'add' : 'remove']('active')
    Flip.from(state, { duration: 0.5, ease: 'power2.out' })
  }, [active])

  return (
    <Button
      asChild
      variant="secondary"
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      className={cn('hover:before:bg-transparent', className)}
      endIcon={
        <span ref={wrapper} className="relative [&.active]:static">
          <span
            ref={rect}
            className="absolute left-0 top-0 !m-0 size-full rounded-[0.5rem] bg-dark dark:bg-white"
          />
          <svg className="relative size-[2.6rem]" viewBox="0 0 26 26" fill="none">
            <rect
              className="fill-dark dark:fill-white"
              x="0.5"
              y="0.5"
              width="25"
              height="25"
              rx="2"
            />
            <rect
              className={cn(
                'fill-white dark:fill-dark',
                'transition-transform duration-500 ease-out-cubic',
                'origin-center scale-0 group-hover/button:scale-100'
              )}
              x="0.5"
              y="0.5"
              width="25"
              height="25"
              rx="2"
            />
            <path
              d="M12.272 16.5V13.728H9.5V12.272H12.272V9.5H13.728V12.272H16.5V13.728H13.728V16.5H12.272Z"
              className={cn(
                'fill-white dark:fill-dark',
                'origin-center transition-all duration-300 ease-out-cubic',
                'group-hover/button:rotate-90 group-hover/button:fill-dark dark:group-hover/button:fill-white'
              )}
            />
          </svg>
        </span>
      }
      {...props}
    >
      <Link href={href} target={target} scroll={false}>
        <ButtonMask className="z-10 after:text-white dark:after:text-dark">
          <Typography variant="button">{children}</Typography>
        </ButtonMask>
      </Link>
    </Button>
  )
}

export default ButtonMore
